import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ShippingConfig,
  ShippingCourier,
  ShippingPackage,
  ShippingZone,
} from '@mlm/shared';
import { Button, Card, CardContent, CardHeader, CardTitle, Checkbox, Input } from '@mlm/ui';
import {
  courierLogoUrl,
  getShippingConfig,
  publishShippingConfig,
  uploadCourierLogo,
} from '../lib/api';

const EMPTY: ShippingConfig = { zones: [], routeMultipliers: {}, couriers: [] };

/** Pull a human message out of an axios/Fastify error for the publish banner. */
function publishErrorMessage(err: unknown): string {
  const data = (err as { response?: { data?: unknown } } | null)?.response?.data as
    | { message?: string; error?: string }
    | undefined;
  return data?.message || data?.error || 'check that every courier/zone has an ID and label.';
}

/**
 * Simplified Shipping editor (no raw JSON). Per courier: packages with
 * dimensions + a single base rate. Plus ONE shared zone-multiplier grid:
 * fee = package.baseRate × routeMultipliers[origin][destination].
 */
export function ShippingPage() {
  const qc = useQueryClient();
  const cfgQ = useQuery({ queryKey: ['admin-shipping-config'], queryFn: getShippingConfig });
  const [cfg, setCfg] = useState<ShippingConfig | null>(null);
  const [savedVersion, setSavedVersion] = useState<number | null>(null);

  useEffect(() => {
    if (cfgQ.data && !cfg) setCfg(structuredClone(cfgQ.data));
    if (cfgQ.isFetched && cfgQ.data === null && !cfg) setCfg(structuredClone(EMPTY));
  }, [cfgQ.data, cfgQ.isFetched, cfg]);

  const publish = useMutation({
    mutationFn: () => publishShippingConfig(cfg!),
    meta: { successMessage: 'Shipping published.' },
    onSuccess: ({ version }) => {
      setSavedVersion(version);
      qc.invalidateQueries({ queryKey: ['admin-shipping-config'] });
      setTimeout(() => setSavedVersion(null), 2500);
    },
  });

  if (!cfg) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const zoneIds = cfg.zones.map((z) => z.id);
  const update = (next: ShippingConfig) => setCfg({ ...next });

  const addZone = () => update({ ...cfg, zones: [...cfg.zones, { id: '', label: '', regCodes: [] }] });
  const addCourier = () =>
    update({
      ...cfg,
      couriers: [
        ...cfg.couriers,
        { id: '', label: '', enabled: true, cod: false, branchOnly: false, packages: [] },
      ],
    });

  const setMultiplier = (o: string, d: string, v: number) => {
    const rm = { ...cfg.routeMultipliers, [o]: { ...(cfg.routeMultipliers[o] ?? {}), [d]: v } };
    update({ ...cfg, routeMultipliers: rm });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Shipping</h1>
          <p className="text-sm text-muted-foreground">
            Per-courier base rates × a shared zone multiplier. Publishing creates a new version.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {savedVersion && <span className="text-sm text-success">Published v{savedVersion}</span>}
          <Button onClick={() => publish.mutate()} disabled={publish.isPending}>
            {publish.isPending ? 'Publishing…' : 'Publish'}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle>Zones</CardTitle>
          <Button size="sm" variant="outline" onClick={addZone}>+ Zone</Button>
        </CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted-foreground border-b">
              <tr><th className="py-1 pr-2">ID</th><th className="py-1 pr-2">Label</th><th className="py-1 pr-2">Region codes</th><th className="w-8"></th></tr>
            </thead>
            <tbody>
              {cfg.zones.map((z, i) => (
                <ZoneRow
                  key={i}
                  zone={z}
                  onChange={(nz) => { const zones = [...cfg.zones]; zones[i] = nz; update({ ...cfg, zones }); }}
                  onRemove={() => update({ ...cfg, zones: cfg.zones.filter((_, j) => j !== i) })}
                />
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Zone multiplier grid</CardTitle>
          <p className="text-xs text-muted-foreground">fee = package base rate × multiplier[origin][destination]. Local route = 1.0.</p>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="text-sm">
            <thead>
              <tr>
                <th className="px-2 py-1 text-xs text-muted-foreground text-left">origin ↓ / dest →</th>
                {zoneIds.map((d) => <th key={d} className="px-2 py-1 text-xs">{d}</th>)}
              </tr>
            </thead>
            <tbody>
              {zoneIds.map((o) => (
                <tr key={o}>
                  <td className="px-2 py-1 text-xs font-medium">{o}</td>
                  {zoneIds.map((d) => (
                    <td key={d} className="px-1 py-1">
                      <Input
                        className="h-7 w-20"
                        value={cfg.routeMultipliers[o]?.[d] ?? ''}
                        onChange={(e) => setMultiplier(o, d, Number(e.target.value) || 0)}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium">Couriers</h2>
        <Button size="sm" variant="outline" onClick={addCourier}>+ Courier</Button>
      </div>

      {cfg.couriers.map((c, i) => (
        <CourierCard
          key={i}
          courier={c}
          onChange={(nc) => { const couriers = [...cfg.couriers]; couriers[i] = nc; update({ ...cfg, couriers }); }}
          onRemove={() => update({ ...cfg, couriers: cfg.couriers.filter((_, j) => j !== i) })}
        />
      ))}

      {publish.error && (
        <p className="text-sm text-destructive">
          Publish failed: {publishErrorMessage(publish.error)}
        </p>
      )}
    </div>
  );
}

function ZoneRow({ zone, onChange, onRemove }: { zone: ShippingZone; onChange: (z: ShippingZone) => void; onRemove: () => void }) {
  return (
    <tr className="border-b">
      <td className="py-1 pr-2"><Input className="h-8" value={zone.id} onChange={(e) => onChange({ ...zone, id: e.target.value })} /></td>
      <td className="py-1 pr-2"><Input className="h-8" value={zone.label} onChange={(e) => onChange({ ...zone, label: e.target.value })} /></td>
      <td className="py-1 pr-2">
        <Input
          className="h-8"
          value={zone.regCodes.join(', ')}
          onChange={(e) => onChange({ ...zone, regCodes: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
        />
      </td>
      <td className="py-1 text-right"><button className="text-muted-foreground hover:text-destructive text-xs" onClick={onRemove}>✕</button></td>
    </tr>
  );
}

function CourierCard({ courier, onChange, onRemove }: { courier: ShippingCourier; onChange: (c: ShippingCourier) => void; onRemove: () => void }) {
  const setField = <K extends keyof ShippingCourier>(k: K, v: ShippingCourier[K]) => onChange({ ...courier, [k]: v });
  const addPackage = () =>
    setField('packages', [...courier.packages, { key: '', label: '', baseRate: 0 } as ShippingPackage]);

  // Local object-URL preview for a just-picked logo (the public read endpoint
  // only resolves once the config is published), falling back to the published
  // logo when one exists.
  const [localLogo, setLocalLogo] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoSrc = localLogo ?? (courier.logoKey && courier.id ? courierLogoUrl(courier.id) : null);

  async function onLogoPick(file: File) {
    setLocalLogo(URL.createObjectURL(file));
    setUploadingLogo(true);
    try {
      // Pass the courier id so the server persists the logo immediately when
      // this courier already exists in the published config.
      const { key } = await uploadCourierLogo(file, courier.id || undefined);
      setField('logoKey', key);
    } finally {
      setUploadingLogo(false);
    }
  }

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="text-base">{courier.label || courier.id || 'New courier'}</CardTitle>
        <button className="text-muted-foreground hover:text-destructive text-sm" onClick={onRemove}>Remove courier</button>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Field label="ID"><Input className="h-8" value={courier.id} onChange={(e) => setField('id', e.target.value)} /></Field>
          <Field label="Label"><Input className="h-8" value={courier.label} onChange={(e) => setField('label', e.target.value)} /></Field>
          <Field label="Flat fee (optional)">
            <Input className="h-8" value={courier.flatFee ?? ''} onChange={(e) => setField('flatFee', e.target.value === '' ? undefined : Number(e.target.value))} />
          </Field>
          <div className="flex items-end gap-3 text-xs">
            <label className="flex items-center gap-1.5">
              <Checkbox checked={courier.enabled} onCheckedChange={(v) => setField('enabled', v === true)} /> Enabled
            </label>
            <label className="flex items-center gap-1.5">
              <Checkbox checked={courier.cod} onCheckedChange={(v) => setField('cod', v === true)} /> COD
            </label>
            <label className="flex items-center gap-1.5">
              <Checkbox checked={courier.branchOnly} onCheckedChange={(v) => setField('branchOnly', v === true)} /> Pickup
            </label>
          </div>
        </div>

        <Field label="Logo (shown beside the courier name at checkout)">
          <div className="flex items-center gap-3">
            {logoSrc ? (
              <img src={logoSrc} alt="" className="h-10 w-10 rounded border object-contain bg-white" />
            ) : (
              <div className="h-10 w-10 rounded border bg-muted" />
            )}
            <input
              type="file"
              accept="image/*"
              className="text-xs"
              disabled={uploadingLogo}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void onLogoPick(f);
              }}
            />
            {uploadingLogo && <span className="text-xs text-muted-foreground">Uploading…</span>}
            {courier.logoKey && !uploadingLogo && (
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-destructive"
                onClick={() => { setLocalLogo(null); setField('logoKey', undefined); }}
              >
                Remove
              </button>
            )}
          </div>
          <p className="mt-1 text-[11px] text-muted-foreground">
            Saved on upload for existing couriers; a brand-new courier saves with Publish.
            Recommended: a small square PNG.
          </p>
        </Field>

        <Field label="Tracking URL template (optional)">
          <Input
            className="h-8"
            value={courier.trackingUrlTemplate ?? ''}
            onChange={(e) => setField('trackingUrlTemplate', e.target.value || undefined)}
            placeholder="https://courier.example.com/track?n={tracking}"
          />
          <p className="mt-1 text-[11px] text-muted-foreground">
            Use <code className="rounded bg-muted px-1">{'{tracking}'}</code> where the tracking
            number goes. When an order ships, the member's clickable tracking link is built from this
            + the number entered — no per-order link needed.
          </p>
        </Field>

        {!courier.branchOnly && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-muted-foreground">Packages (chosen by dimension + weight fit; base rate × zone multiplier)</span>
              <button className="text-xs hover:underline" onClick={addPackage}>+ Package</button>
            </div>
            <table className="w-full text-xs">
              <thead className="text-left text-muted-foreground border-b">
                <tr>
                  <th className="py-1 pr-2">Key</th><th className="py-1 pr-2">Label</th>
                  <th className="py-1 pr-2">Max L</th><th className="py-1 pr-2">Max W</th><th className="py-1 pr-2">Max H</th><th className="py-1 pr-2">Max kg</th>
                  <th className="py-1 pr-2">Base rate</th><th className="w-6"></th>
                </tr>
              </thead>
              <tbody>
                {courier.packages.map((p, i) => {
                  const setPkg = (np: ShippingPackage) => { const packages = [...courier.packages]; packages[i] = np; setField('packages', packages); };
                  return (
                    <tr key={i} className="border-b">
                      <td className="py-1 pr-2"><Input className="h-7" value={p.key} onChange={(e) => setPkg({ ...p, key: e.target.value })} /></td>
                      <td className="py-1 pr-2"><Input className="h-7" value={p.label} onChange={(e) => setPkg({ ...p, label: e.target.value })} /></td>
                      {(['maxLengthCm', 'maxWidthCm', 'maxHeightCm', 'maxWeightKg'] as const).map((f) => (
                        <td key={f} className="py-1 pr-2">
                          <Input className="h-7 w-16" value={(p[f] as number | undefined) ?? ''} onChange={(e) => setPkg({ ...p, [f]: e.target.value === '' ? undefined : Number(e.target.value) })} />
                        </td>
                      ))}
                      <td className="py-1 pr-2"><Input className="h-7 w-20" value={p.baseRate} onChange={(e) => setPkg({ ...p, baseRate: Number(e.target.value) || 0 })} /></td>
                      <td className="text-right"><button className="text-muted-foreground hover:text-destructive" onClick={() => setField('packages', courier.packages.filter((_, j) => j !== i))}>✕</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}
